import React, { useContext, useEffect, useState } from 'react'
import { AuthContext } from '../../context/auth/auth-context'
import useWebSocket, { ReadyState } from 'react-use-websocket'
import { v4 as uuid_v4 } from 'uuid'
import clsx from 'clsx'
import { DataGridPro, type GridColDef, type GridSortModel } from '@mui/x-data-grid-pro'
import PowerOffIcon from '@mui/icons-material/PowerOff'
import { Button, Card, CardContent, Stack, Typography } from '@mui/material'
import { type StrategyEvent, LEVELS } from '../../models/event/strategy-event'
import { formatToLocalMilliTimestampFromUTCString } from '../../utils/time-utils'
import Loader from '../../components/loader/loader'

const columnsEvent: GridColDef[] = [
  {
    field: 'timestamp',
    headerName: 'Timestamp',
    type: 'string',
    width: 150,
    valueFormatter: (value) => formatToLocalMilliTimestampFromUTCString(value),
  },
  {
    field: 'level',
    headerName: 'Level',
    type: 'string',
    width: 80,
    cellClassName: (params) => {
      return clsx('level', {
        info: params.value === LEVELS.INFO,
        warn: params.value === LEVELS.WARN,
        critical: params.value === LEVELS.CRITICAL,
      })
    },
  },
  { field: 'type', headerName: 'Type', type: 'string', width: 80 },
  { field: 'message', headerName: 'Message', type: 'string', width: 150 },
]

const EVENT_SORTING: GridSortModel = [
  { field: 'timestamp', sort: 'desc' },
]

interface EventDataGridProps {
  eventUrl: string | null
}

interface Message {
  channelId: string
  data: StrategyEvent
}

const EventDataGrid: React.FC<EventDataGridProps> = ({ eventUrl }) => {
  const [events, setEvents] = useState<StrategyEvent[]>([])

  const { isAuthorized, accessToken } = useContext(AuthContext)
  useEffect(() => {
    if (isAuthorized) {
      console.debug('[Events] Ready to connect')
      setShouldConnect(true)
    }
  }, [isAuthorized])

  useEffect(() => {
    if (eventUrl == null) {
      console.debug('[Events] Skipped connecting to data stream (reason: null url)')
      setAttemptedApiConnect(true)
    }
  }, [eventUrl])

  const [attemptedApiConnect, setAttemptedApiConnect] = useState<boolean>(false)
  const [reconnectAttemptNo, setReconnectAttemptNo] = useState<number>(0)
  const [maxAttemptedReconnects, setMaxAttemptedReconnects] = useState<boolean>(false)
  const [shouldConnect, setShouldConnect] = useState<boolean>(false)

  const { lastJsonMessage, readyState }: { lastJsonMessage: Message | null, readyState: number } = useWebSocket(
    eventUrl,
    {
      share: true,
      protocols: ['Authorization', accessToken ?? 'no-token'],
      onOpen: () => {
        console.log('[Events] WebSocket connection established')
        setAttemptedApiConnect(true)
        setReconnectAttemptNo(0)
      },
      onClose: (event) => {
        console.log('[Events] WebSocket connection closed', event)
        setAttemptedApiConnect(true)
      },
      onError: (error) => {
        console.error('[Events] Error encountered when processing message: ', error)
      },
      onReconnectStop: () => {
        console.error('[Events] Max reconnection attempts reached')
        setMaxAttemptedReconnects(true)
        setShouldConnect(false)
      },
      filter: (message) => {
        const parsedData = JSON.parse(message.data)
        return parsedData?.channelId === 'event'
      },
      shouldReconnect: () => {
        const newAttemptCount = reconnectAttemptNo + 1
        setReconnectAttemptNo(newAttemptCount)
        return true
      },
      retryOnError: true,
    },
    shouldConnect,
  )

  useEffect(() => {
    if (eventUrl == null || lastJsonMessage == null) {
      return
    }

    const newData = {
      id: uuid_v4(),
      ...lastJsonMessage.data,
    }
    const newEvents = events.concat(newData)
    setEvents(newEvents)
  }, [eventUrl, lastJsonMessage])

  return (
    <Card
      variant='outlined'
      sx={{
        width: 1,
        height: '100%',
        padding: 1,
        margin: 0,
        background: 'transparent',
        borderRadius: 0,
        '& .level.info': {
          color: 'info.main',
        },
        '& .level.warn': {
          color: 'warning.main',
        },
        '& .level.critical': {
          color: 'error.main',
        },
      }}
    >
      <CardContent
        sx={{
          height: 'calc(100% - 24px)',
          padding: 0,
          margin: 0,
        }}
      >
        <Typography variant='h6' fontWeight='bold' marginBottom={1}>
          Events
        </Typography>

        {
          readyState === ReadyState.OPEN
            ? <Stack height={1}>
              <DataGridPro
                columns={columnsEvent}
                rows={events}
                rowHeight={24}
                sortModel={EVENT_SORTING}
                // slots={{
                //   toolbar: EventGridToolbar
                // }}
                // slotProps={{
                //   toolbar: { csvOptions: { fileName: 'events' } }
                // }}
              />
            </Stack>
            : <Stack alignItems='center' justifyContent='center' gap={1}>
              {
                attemptedApiConnect
                  ? <PowerOffIcon color='error' fontSize='large'/>
                  : <Loader/>
              }

              {
                (!maxAttemptedReconnects && reconnectAttemptNo > 0)
                  ? <Typography color='primary' align='center'>
                    {`Attempted reconnection ${reconnectAttemptNo} time${reconnectAttemptNo > 1 ? 's' : ''}`}
                  </Typography>
                  : null
              }

              {
                maxAttemptedReconnects
                  ? <>
                    <Typography color='primary' align='center'>
                      Max auto-reconnection attempts reached
                    </Typography>
                    <Button
                      variant='outlined'
                      size='small'
                      onClick={() => {
                        setReconnectAttemptNo(0)
                        setMaxAttemptedReconnects(false)
                        setShouldConnect(true)
                      }}
                    >
                      Reconnect
                    </Button>
                  </>
                  : null
              }
            </Stack>
        }
      </CardContent>
    </Card>
  )
}

export default EventDataGrid
